import React, { useState } from 'react'; // NEW: Import useState
import { Product } from '../types/costwand.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import Pagination from './ui/Pagination.tsx';
import { Link } from 'react-router-dom'; // Import Link
import { Spinner } from './ui/spinner.tsx'; // Import Spinner
import { apiClient } from '../api/apiClient.ts'; // NEW: Import apiClient

interface ProductTableProps {
  products: Product[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
  loading: boolean;
  error: string | null;
  lookupLoading: boolean;
  baseEndpoint: string; // NEW: Add baseEndpoint prop
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onEdit,
  onDelete,
  loading,
  error,
  lookupLoading,
  baseEndpoint, // NEW: Destructure baseEndpoint
}) => {
  const [exportLoading, setExportLoading] = useState(false); // NEW: State for export loading

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: any) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading)
    return (
      <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading Products...</p>
    );
  if (error)
    return (
      <p className="text-center text-destructive">Error: {error}</p>
    );
  if (products.length === 0 && !loading)
    return (
      <p className="text-center text-muted-foreground">No Products found.</p>
    );

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4"> {/* NEW: Wrapper for export button */}
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead>Article No.</TableHead>
            <TableHead>Vendor</TableHead>
            <TableHead>Family</TableHead>
            <TableHead>Cost</TableHead>
            <TableHead>Wholesale</TableHead>
            <TableHead>Retail</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => (
            <TableRow key={product.id}>
              <TableCell>{product.description}</TableCell>
              <TableCell>{product.article_number}</TableCell>
              <TableCell>
                {product._labels?.vendor_id || `ID: ${product.vendor_id}`}
              </TableCell>
              <TableCell>
                {product._labels?.family_id || `ID: ${product.family_id}`}
              </TableCell>
              <TableCell>${product.cost.toFixed(4)}</TableCell>
              <TableCell>{product.markup_perc_wholesale?.toFixed(2) + '%' || 'N/A'}</TableCell>
              <TableCell>{product.markup_perc_retail?.toFixed(2) + '%' || 'N/A'}</TableCell>
              <TableCell className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(product)}
                >
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  asChild
                >
                  <Link to={`/composition-edit/${product.id}`}>
                    Convert to Composite
                  </Link>
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(product.id!)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        disabled={lookupLoading}
      />
    </div>
  );
};

export default ProductTable;